import React, { useState } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Divider,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Link,
} from '@mui/material'
import { Upload as UploadIcon } from '@mui/icons-material'

// Define the form section type
interface FormField {
  id?: string
  label: string
  source: string
  required?: boolean
  value?: string
  fieldType?: 'yesno' | 'dropdown' | 'attachment'
  options?: string[]
  attachments?: Array<{
    id: string
    filename: string
    url?: string
  }>
  vendorId?: number
  questionnaireId?: number
}

interface FormSection {
  id?: string
  title: string
  fields: FormField[]
}

// Attachment field renderer component
interface AttachmentFieldRendererProps {
  field: FormField
  readOnly: boolean
}

const AttachmentFieldRenderer: React.FC<AttachmentFieldRendererProps> = ({
  field,
  readOnly,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Function to handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target
    if (!files || files.length === 0) return

    const file = files[0]
    setUploading(true)
    setUploadError(null)

    try {
      // Create form data for the file upload
      const formData = new FormData()
      formData.append('document', file)
      formData.append('field_id', field.id || field.source)

      // Get the token from the URL if this is a public questionnaire
      const token = window.location.pathname.split('/').pop()

      // Determine the API endpoint based on whether this is a public questionnaire
      const apiEndpoint =
        token && token.length > 10
          ? `/api/v1/questionnaire/${token}/attachments`
          : `/api/v1/questionnaires/vendors/${field.vendorId}/${field.questionnaireId}/attachments`

      // Upload the file
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`)
      }

      const data = await response.json()

      // Update the field with the new attachment
      if (!field.attachments) {
        field.attachments = []
      }

      field.attachments.push({
        id: data.id,
        filename: data.original_filename,
        url: `/api/v1/questionnaires/vendors/${field.vendorId}/${field.questionnaireId}/attachments/${data.id}`,
      })

      // Clear the file input
      event.target.value = ''
    } catch (error) {
      console.error('File upload error:', error)
      setUploadError(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Box>
      {!readOnly && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={
              uploading ? <CircularProgress size={20} /> : <UploadIcon />
            }
            disabled={uploading || readOnly}
          >
            <Typography>
              {uploading ? 'Uploading...' : 'Add Attachment'}
            </Typography>
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFileUpload}
              disabled={uploading || readOnly}
            />
          </Button>
          {uploadError && (
            <Typography
              color="error"
              variant="caption"
              sx={{ display: 'block', mt: 1 }}
            >
              {uploadError}
            </Typography>
          )}
        </Box>
      )}

      {field.attachments && field.attachments.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Uploaded Files:
          </Typography>
          {field.attachments.map((attachment, index) => (
            <Box key={attachment.id || index} sx={{ mb: 1 }}>
              <Link
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {attachment.filename}
              </Link>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {readOnly ? 'No files uploaded' : 'No files uploaded yet'}
        </Typography>
      )}
    </Box>
  )
}

interface VendorFormLayoutProps {
  formSections: FormSection[]
  formName?: string
  formDescription?: string
  readOnly?: boolean
  renderField?: (field: FormField) => React.ReactNode
}

/**
 * A reusable component for displaying vendor form layouts
 * Used in VendorCreate, VendorEdit, VendorShow, and OrganisationVendorForm preview
 */
const VendorFormLayout: React.FC<VendorFormLayoutProps> = ({
  formSections,
  formName,
  formDescription,
  readOnly = false,
  renderField,
}) => {
  // Default field renderer for read-only mode
  const defaultReadOnlyFieldRenderer = (field: FormField) => {
    return field.value ?? 'N/A'
  }

  // Default field renderer for editable mode (used in VendorCreate and VendorEdit)
  const defaultEditableFieldRenderer = (field: FormField) => {
    // For attachment fields
    if (field.fieldType === 'attachment') {
      return <AttachmentFieldRenderer field={field} readOnly={readOnly} />
    }

    // For yes/no fields (used in prequalification questionnaires)
    if (field.fieldType === 'yesno') {
      return (
        <TextField
          select
          label={field.label}
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={readOnly}
          InputProps={{
            readOnly,
          }}
          helperText={field.required ? 'Required' : 'Optional'}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </TextField>
      )
    }

    // For dropdown fields (used in risk assessments)
    if (
      field.fieldType === 'dropdown' &&
      field.options &&
      field.options.length > 0
    ) {
      return (
        <TextField
          select
          label={field.label}
          name={field.source}
          fullWidth
          variant="outlined"
          required={field.required}
          disabled={readOnly}
          InputProps={{
            readOnly,
          }}
          helperText={field.required ? 'Required' : 'Optional'}
          SelectProps={{
            native: true,
          }}
        >
          <option value="">Select an option</option>
          {field.options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </TextField>
      )
    }

    // Default text field for other fields
    return (
      <TextField
        label={field.label}
        name={field.source}
        fullWidth
        variant="outlined"
        required={field.required}
        disabled={readOnly}
        InputProps={{
          readOnly,
        }}
        helperText={field.required ? 'Required' : 'Optional'}
      />
    )
  }

  // Use the provided renderField function or default to read-only or editable based on readOnly prop
  const fieldRenderer =
    renderField ||
    (readOnly ? defaultReadOnlyFieldRenderer : defaultEditableFieldRenderer)

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      {formName && (
        <Typography variant="h5" gutterBottom>
          {formName}
        </Typography>
      )}

      {formDescription && (
        <Typography variant="body1" color="textSecondary" paragraph>
          {formDescription}
        </Typography>
      )}

      {formName && <Divider sx={{ my: 2 }} />}

      {formSections.map((section, sectionIndex) => (
        <Box key={section.id || sectionIndex} sx={{ mb: 4 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      bgcolor: '#0077c8',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      p: 1.5,
                    }}
                  >
                    {section.title}
                  </TableCell>
                </TableRow>

                {section.fields.map((field, fieldIndex) => (
                  <TableRow key={field.id || `${field.source}-${fieldIndex}`}>
                    <TableCell
                      sx={{
                        bgcolor: '#d9d9d9',
                        width: '35%',
                        p: 0.25,
                        textAlign: 'left',
                        verticalAlign: 'middle',
                      }}
                    >
                      {field.label}
                      {field.required && !readOnly && (
                        <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                      )}
                    </TableCell>
                    <TableCell sx={{ p: 0.25 }}>
                      {fieldRenderer(field)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  )
}

export default VendorFormLayout
